/**
 * --------------------------------------------------------------------------
 * NJ : Collapse.js
 * --------------------------------------------------------------------------
 */

import {
  Data, EventHandler, Manipulator, SelectorEngine, Util
} from 'nj-utils';
import { coreConfig, eventsName } from 'nj-components/core.config';
import AbstractComponent from 'nj-components/AbstractComponent';

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME                = `${coreConfig.KEY_PREFIX}-collapse`;
export const DATA_KEY     = `${coreConfig.KEY_PREFIX}.collapse`;
const EVENT_KEY           = `.${DATA_KEY}`;
const DATA_API_KEY        = coreConfig.KEY_PREFIX;

export const CLASS_NAME = {
  show       : 'show',
  collapse   : `${coreConfig.KEY_PREFIX}-collapse`,
  collapsing : `${coreConfig.KEY_PREFIX}-collapsing`,
  collapsed  : `${coreConfig.KEY_PREFIX}-collapsed`
};

export const EVENT = {
  show           : `${eventsName.show}${EVENT_KEY}`,
  shown          : `${eventsName.shown}${EVENT_KEY}`,
  hide           : `${eventsName.hide}${EVENT_KEY}`,
  hidden         : `${eventsName.hidden}${EVENT_KEY}`,
  clickDataApi   : `${eventsName.click}${EVENT_KEY}${DATA_API_KEY}`
};

const DEFAULT_OPTIONS = {
  toggle : false,
  parent : ''
};

const DEFAULT_TYPE = {
  toggle : 'boolean',
  parent : '(string|element)'
};

const DIMENSION = {
  width  : 'width',
  height : 'height'
};

const SELECTOR = {
  default     : `.${CLASS_NAME.collapse}`,
  actives     : `.${CLASS_NAME.show}, .${CLASS_NAME.collapsing}`,
  dataToggle  : '[data-toggle="collapse"]'
};

class Collapse extends AbstractComponent {
  element;
  isTransitioning;
  options;
  triggerArray;

  constructor(element, options) {
    super(Collapse, element);

    this.element         = element;
    this.isTransitioning = false;
    this.options         = this.getOptions(options);
    this.triggerArray    = Util.makeArray(SelectorEngine.find(
      `${SELECTOR.dataToggle}[href="#${element.id}"],` +
      `${SELECTOR.dataToggle}[data-target="#${element.id}"]`
    ));

    const toggleList = Util.makeArray(document.querySelectorAll(SELECTOR.dataToggle));

    for (let i = 0, len = toggleList.length; i < len; i++) {
      const elem = toggleList[i];
      const selector = Util.getSelectorFromElement(elem);

      const filterElement = Util.makeArray(document.querySelectorAll(selector))
        .filter((foundElem) => foundElem === element);

      if (selector !== null && filterElement.length) {
        this.selector = selector;
        this.triggerArray.push(elem);
      }
    }

    this.parent = this.options.parent ? this.getParent() : null;

    if (!this.options.parent) {
      this.addAriaAndCollapsedClass(this.element, this.triggerArray);
    }

    if (this.element) {
      Data.setData(element, DATA_KEY, this);
    }

    if (this.options.toggle) {
      this.toggle();
    }

    Data.setData(element, DATA_KEY, this);
  }

  toggle() {
    if (this.element.classList.contains(CLASS_NAME.show)) {
      this.hide();
    } else {
      this.show();
    }
  }

  show() {
    if (this.isTransitioning || this.element.classList.contains(CLASS_NAME.show)) {
      return;
    }

    let actives;
    let activesData;

    if (this.parent) {
      actives = Util.makeArray(this.parent.querySelectorAll(SELECTOR.actives))
        .filter((elem) => {
          if (typeof this.options.parent === 'string') {
            return elem.getAttribute('data-parent') === this.options.parent;
          }

          return elem.classList.contains(CLASS_NAME.collapse);
        });

      if (actives.length === 0) {
        actives = null;
      }
    }

    const container = SelectorEngine.findOne(this.selector);
    if (actives) {
      const tempActiveData = actives.filter((elem) => container !== elem);
      activesData = tempActiveData[0] ? Data.getData(tempActiveData[0], DATA_KEY) : null;

      if (activesData && activesData.isTransitioning) {
        return;
      }
    }

    const startEvent = EventHandler.trigger(this.element, EVENT.show);
    if (startEvent.defaultPrevented) {
      return;
    }

    if (actives) {
      actives.forEach((elemActive) => {
        if (container !== elemActive) {
          Collapse.collapseInterface(elemActive, 'hide');
        }

        if (!activesData) {
          Data.setData(elemActive, DATA_KEY, null);
        }
      });
    }

    const dimension = this.getDimension();

    this.element.classList.remove(CLASS_NAME.collapse);
    this.element.classList.add(CLASS_NAME.collapsing);

    this.element.style[dimension] = 0;

    if (this.triggerArray.length) {
      this.triggerArray.forEach((element) => {
        element.classList.remove(CLASS_NAME.collapsed);
        element.setAttribute('aria-expanded', true);
      });
    }

    this.setTransitioning(true);

    const complete = () => {
      this.element.classList.remove(CLASS_NAME.collapsing);
      this.element.classList.add(CLASS_NAME.collapse);
      this.element.classList.add(CLASS_NAME.show);

      this.element.style[dimension] = '';

      this.setTransitioning(false);

      EventHandler.trigger(this.element, EVENT.shown);
    };

    const capitalizedDimension = dimension[0].toUpperCase() + dimension.slice(1);
    const scrollSize = `scroll${capitalizedDimension}`;
    const transitionDuration = Util.getTransitionDurationFromElement(this.element);

    EventHandler.one(this.element, Util.TRANSITION_END, complete);

    Util.emulateTransitionEnd(this.element, transitionDuration);
    this.element.style[dimension] = `${this.element[scrollSize]}px`;
  }

  hide() {
    if (this.isTransitioning || !this.element.classList.contains(CLASS_NAME.show)) {
      return;
    }

    const startEvent = EventHandler.trigger(this.element, EVENT.hide);
    if (startEvent.defaultPrevented) {
      return;
    }

    const dimension = this.getDimension();

    this.element.style[dimension] = `${this.element.getBoundingClientRect()[dimension]}px`;

    Util.reflow(this.element);

    this.element.classList.add(CLASS_NAME.collapsing);
    this.element.classList.remove(CLASS_NAME.collapse);
    this.element.classList.remove(CLASS_NAME.show);

    const triggerArrayLength = this.triggerArray.length;
    if (triggerArrayLength > 0) {
      for (let i = 0; i < triggerArrayLength; i++) {
        const trigger = this.triggerArray[i];
        const selector = Util.getSelectorFromElement(trigger);

        if (selector !== null) {
          const elem = SelectorEngine.findOne(selector);

          if (!elem.classList.contains(CLASS_NAME.show)) {
            trigger.classList.add(CLASS_NAME.collapsed);
            trigger.setAttribute('aria-expanded', false);
          }
        }
      }
    }

    this.setTransitioning(true);

    const complete = () => {
      this.setTransitioning(false);
      this.element.classList.remove(CLASS_NAME.collapsing);
      this.element.classList.add(CLASS_NAME.collapse);
      EventHandler.trigger(this.element, EVENT.hidden);
    };

    this.element.style[dimension] = '';
    const transitionDuration = Util.getTransitionDurationFromElement(this.element);

    EventHandler.one(this.element, Util.TRANSITION_END, complete);
    Util.emulateTransitionEnd(this.element, transitionDuration);
  }

  setTransitioning(isTransitioning) {
    this.isTransitioning = isTransitioning;
  }

  dispose() {
    Data.removeData(this.element, DATA_KEY);

    this.options         = null;
    this.parent          = null;
    this.element         = null;
    this.triggerArray    = null;
    this.isTransitioning = null;
  }

  getDimension() {
    const hasWidth = this.element.classList.contains(DIMENSION.width);
    return hasWidth ? DIMENSION.width : DIMENSION.height;
  }

  getParent() {
    let parent;

    if (Util.isElement(this.options.parent)) {
      parent = this.options.parent;
    } else if (this.options.parent) {
      parent = SelectorEngine.findOne(this.options.parent);
    }

    const selector =
      `[data-toggle="collapse"][data-parent="${this.options.parent}"]`;

    Util.makeArray(SelectorEngine.find(selector, parent))
      .forEach((element) => {
        this.addAriaAndCollapsedClass(
          this.getTargetFromElement(element),
          [element]
        );
      });

    return parent;
  }

  addAriaAndCollapsedClass(element, triggerArray) {
    if (element) {
      const isOpen = element.classList.contains(CLASS_NAME.show);

      if (triggerArray.length) {
        triggerArray.forEach((elem) => {
          if (!isOpen) {
            elem.classList.add(CLASS_NAME.collapsed);
          } else {
            elem.classList.remove(CLASS_NAME.collapsed);
          }
          elem.setAttribute('aria-expanded', isOpen);
        });
      }
    }
  }

  getOptions(options) {
    options = {
      ...DEFAULT_OPTIONS,
      ...options
    };
    options.toggle = Boolean(options.toggle); // Coerce string values
    Util.typeCheckConfig(NAME, options, DEFAULT_TYPE);
    return options;
  }

  static getTargetFromElement(element) {
    const selector = Util.getSelectorFromElement(element);
    return selector ? document.querySelector(selector) : null;
  }

  static collapseInterface(element, options) {
    let data = Data.getData(element, DATA_KEY);
    const cfg = {
      ...DEFAULT_OPTIONS,
      ...Manipulator.getDataAttributes(element),
      ...typeof options === 'object' && options ? options : {}
    };

    if (!data && cfg.toggle && /show|hide/.test(options)) {
      cfg.toggle = false;
    }

    if (!data) {
      data = new Collapse(element, cfg);
    }

    if (typeof options === 'string') {
      if (typeof data[options] === 'undefined') {
        throw new Error(`No method named "${options}"`);
      }
      data[options]();
    }
  }

  static getInstance(element) {
    return Data.getData(element, DATA_KEY);
  }

  static init(options) {
    return super.init(this, options, SELECTOR.default);
  }

  static selector = SELECTOR.default;
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */

// eslint-disable-next-line prefer-arrow-callback
EventHandler.on(document, EVENT.clickDataApi, SELECTOR.dataToggle, function (event) {
  // preventDefault only for <a> elements (which change the URL) not inside the collapsible element
  if (event.target.tagName === 'A') {
    event.preventDefault();
  }

  const triggerData      = Manipulator.getDataAttributes(this);
  const selector         = Util.getSelectorFromElement(this);
  const selectorElements = Util.makeArray(SelectorEngine.find(selector));

  selectorElements.forEach((element) => {
    const data = Data.getData(element, DATA_KEY);
    let options;
    if (data) {
      // update parent attribute
      if (data.parent === null && typeof triggerData.parent === 'string') {
        data.options.parent = triggerData.parent;
        data.parent = data.getParent();
      }
      options = 'toggle';
    } else {
      options = triggerData;
    }

    Collapse.collapseInterface(element, options);
  });
});

export default Collapse;
